// `configureCommerceData` — the injected-config seam (arch design D3; spec
// `monorepo-commerce-data-core`). Packages are variant-agnostic and store-free
// (G1/F5): the app injects the reactive auth-token hook and its queryClient at
// bootstrap (`src/composition/hosts/commerce/commerce-data-wiring.ts`). Transport is NOT part of
// this seam — `data/api` reads the app's configured clients from the
// `@heidi/api/client` registry (E5, ARCFE-805). Precedent: the workspace and
// coding seams, whose shapes this mirrors deliberately.
//
// Born at D1 with only the structural client view. `useAuthToken` and
// `queryClient` are added by the D2 cohorts that need them (S2/S4) rather
// than up front: an unused seam field is a promise the package has not yet
// had to keep.
//
// The tier, billing and trial reads cross here too, by ruling 65: an ACTION is
// injected whole with its app reactions, a VALUE as a resolved value or a hook
// returning one. Read them through `./injected-billing`, never the app hook.
// Optional, and the reader throws NAMING the member — a partial test double
// fails by name rather than as `undefined`.

import { type QueryClient } from '@tanstack/react-query';

import { type SubscriptionStatus, type SubscriptionTier } from '@heidi/schema/ml-scribe';

import { type BillingSeatType, type HeidiVersion } from '@heidi/schema/ml-scribe';
import { type QueryUserBillingInfoResponse } from './query/useQueryUserBillingInfo';

/**
 * Minimal structural view of the scribe v2 axios client that `data/api`
 * pulls from the `@heidi/api/client` registry — kept structural so this
 * package's public fn signatures never name axios types (declaration
 * portability in the standalone build; the workspace lane's rule, adopted
 * here from birth rather than retrofitted after it bit).
 */
export interface CommerceHttpClient {
  get<T>(
    url: string,
    config?: { params?: Record<string, unknown>; timeout?: number }
  ): Promise<{ data: T }>;
  post<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  patch<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  delete<T = unknown>(
    url: string,
    config?: { params?: Record<string, unknown> }
  ): Promise<{ data: T }>;
}

/**
 * The injected app config. D2 (ARCFE-972) opens it with `queryClient` only —
 * the S4 blocker class (`@/utils/reactQueryClient` ×3) measured at D-scoping.
 * `useAuthToken` still waits for a cohort that needs it: the seam grows by
 * measured demand, not by anticipation.
 */
/**
 * The trial-countdown math the billing query derives its display counters
 * from. Injected rather than moved: both functions bottom out in
 * `@/lib/parseTimeStamp`, which reads the ACCOUNT TIMEZONE and is documented
 * in `@heidi/utils/dates` as staying app-side for exactly that reason. Their
 * only consumer is the billing query, so they are commerce logic — but they
 * cannot cross until the timezone read has a package home (D3, ARCFE-977).
 */
export interface TrialCalculators {
  calculateTrialDaysLeft: (
    isInFreeTrial: boolean,
    trialEnd: string | null | undefined,
    maxDays?: number
  ) => number;
  calculateTrialHoursLeft: (
    isInFreeTrial: boolean,
    trialEnd: string | null | undefined
  ) => number;
}

export interface CommerceDataConfig {
  /** App-resolved Stripe redirect base for `getStripeLink`. */
  resolveStripeRedirectBase: () => string;
  trialCalculators: TrialCalculators;
  /**
   * The reactive auth token, injected as a HOOK rather than a getter so a
   * gated query re-evaluates on token refresh instead of freezing whatever
   * value happened to exist at first render (the workspace seam's rule).
   * Added at D2 batch 2 (ARCFE-973) for the S2 class — the two product/trial
   * queries that gated on `state.auth.token`.
   */
  useAuthToken: () => string | null | undefined;
  /**
   * The app's shared QueryClient, so package hooks can issue module-scope
   * cache commands (`setQueryData` / `invalidateQueries`) against the SAME
   * cache the app renders from. Injected rather than imported because
   * `@/utils/reactQueryClient` is app surface (F5/G1).
   */
  queryClient: QueryClient;
  /**
   * Mirrors `useQueryUserTier` — the flag-gated tier derivation, resolved
   * app-side because it reads an app flag constant, and returned as a value.
   * First sweep reader: `src/features/growth/remote-intro-promo/eligibility/useIsRemoteIntroPromoEligible.ts`.
   */
  useUserTier?: () => UserTierView;
  /**
   * Mirrors `useQueryUserBillingInfo` — the app DELEGATE injected whole, so
   * the dev override, the `enabled` composition and the error toast ride with
   * it; the package's own hook subscribes to the cache and composes none.
   * First sweep reader: `src/features/growth/remote-intro-promo/eligibility/useIsRemoteIntroPromoEligible.ts`.
   */
  useUserBillingInfo?: <T = QueryUserBillingInfoResponse>(
    select?: (data: QueryUserBillingInfoResponse) => T
  ) => UserBillingInfoSubscription<T>;
  /**
   * The quota-exceeded upgrade opener's app inputs: the two
   * paywall atoms' setters bound to the app's jotai default store, and the
   * evidence-launch flag as a call-time THUNK, never a resolved boolean.
   */
  openClinicianUpgradeModal?: () => void;
  openUpgradeProModal?: () => void;
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled?: () => boolean;
  /**
   * Mirrors `useReverseTrial`, MINUS `reverseTrialTitle`,
   * `reverseTrialLostAccessText` and `reverseTrialToastDescription`: copy is
   * an app reaction, so only the booleans and the counts cross.
   * First sweep reader: `src/features/growth/remote-intro-promo/remote-intro-promo-banner/remote-intro-promo-banner.tsx`.
   */
  useReverseTrial?: () => ReverseTrialCounters;
}

/** The three tier values `useQueryUserTier` derives. */
export interface UserTierView {
  userTier: SubscriptionTier | undefined;
  newUserTier: SubscriptionTier | undefined;
  isTogetherOrHigherTier: boolean;
}

/**
 * The app billing delegate's query result, named structurally so this
 * package's public signatures never name react-query's query generics.
 */
export interface UserBillingInfoSubscription<T = QueryUserBillingInfoResponse> {
  data: T | undefined;
  isPending: boolean;
  isLoading: boolean;
  isError: boolean;
  isFetched: boolean;
  /**
   * When the payload landed. `getUserBillingInfo` is persisted, so a restored
   * cache reads as settled while still carrying the previous session's answer;
   * a reader gating on freshness compares this against its own start time.
   */
  dataUpdatedAt: number;
}

/**
 * `useReverseTrial`'s booleans and counts. The three copy strings are absent
 * by design — a package surface renders no app-resolved copy.
 */
export interface ReverseTrialCounters {
  heidiVersion?: HeidiVersion;
  newProductTier?: SubscriptionTier;
  seatType?: BillingSeatType | null;
  isFakeClinicianTrial: boolean;
  isInReverseTrial: boolean;
  reverseTrialTier: SubscriptionTier | undefined;
  isPracticeTrialUser: boolean;
  isEligibleForPracticeTrial: boolean;
  isProTrialUser: boolean;
  trialDaysLeft: number;
  trialHoursLeft: number;
  showHoursInsteadOfDays: boolean;
  showReverseModal: boolean;
  clinicianTrialEnded: boolean;
  isClinicianTrialUser: boolean;
  isClinicianPracticeTrialUser: boolean;
  isScribeTeamUser: boolean;
  clinicianTrialDaysLeft: number;
  clinicianTrialHoursLeft: number;
  /**
   * The schema enum, not a widened `string`: `getReverseTrialCohort` branches on
   * its members, so a widening would have made the cohort read uncheckable at
   * two call sites rather than portable.
   */
  previousStatusBeforeClinicianTrial: SubscriptionStatus | null | undefined;
  isRealStripeClinicianUser: boolean;
  isEvidenceTrialUser: boolean;
  evidenceTrialDaysLeft: number;
  isEvidenceInFreeTrial: boolean;
  clinicianTrialAvailable: boolean;
}

let config: CommerceDataConfig | null = null;

export const configureCommerceData = (next: CommerceDataConfig): void => {
  config = next;
};

/** Fails loudly rather than silently degrading — the workspace/coding rule. */
export const getCommerceDataConfig = (): CommerceDataConfig => {
  if (!config) {
    throw new Error(
      '[commerce/data] not configured — see src/composition/hosts/commerce/commerce-data-wiring.ts'
    );
  }
  return config;
};
